import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DOMPurify from "dompurify";
import { Button, Checkbox } from "@repo/ui";
import {
  StyledScrollableContent,
  StyledCheckboxWrapper,
  StyledErrorText,
  StyledTitleSection,
  StyledHelperText,
  StyledContentWrapper,
} from "./styles";

export interface TermsAndConditionsModalProps {
  isOpen: boolean;
  tncContent: string;
  tncVersion: number;
  onAccept: () => void;
  onClose: () => void;
}

const TermsAndConditionsModal: FC<TermsAndConditionsModalProps> = ({
  isOpen,
  tncContent,
  onAccept,
  onClose,
}) => {
  const intl = useIntl();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [hasBeenChecked, setHasBeenChecked] = useState(false);
  const [showScrollError, setShowScrollError] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const hasScrolledToBottom =
      Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) <= 4;

    if (hasScrolledToBottom) {
      setIsScrolledToBottom(true);
      setShowScrollError(false);
    }
  }, []);

  // If content doesn't overflow, there's nothing to scroll — enable checkbox immediately
  useEffect(() => {
    if (!isOpen) return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      setIsScrolledToBottom(true);
    }
  }, [isOpen, tncContent]);

  const handleCheckboxClick = () => {
    if (!isScrolledToBottom) {
      setShowScrollError(true);
      return;
    }
    const newValue = !isChecked;
    setIsChecked(newValue);
    if (newValue) {
      setHasBeenChecked(true);
    }
  };

  const handleAccept = () => {
    if (isScrolledToBottom && isChecked) {
      onAccept();
      resetState();
    }
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  const resetState = () => {
    setIsScrolledToBottom(false);
    setIsChecked(false);
    setHasBeenChecked(false);
    setShowScrollError(false);
  };

  const sanitizedContent = DOMPurify.sanitize(tncContent, {
    USE_PROFILES: { html: true },
  });

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        },
        "& .MuiDialog-paper": { maxWidth: 800, width: "100%" },
      }}
      fullWidth
      aria-labelledby="tnc-modal-title tnc-modal-title2"
    >
      <IconButton
        aria-label={intl.formatMessage({ id: "tncCloseLabel" })}
        onClick={handleClose}
        sx={{ position: "absolute", top: 8, right: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <StyledContentWrapper>
          <StyledTitleSection>
            <h5 id="tnc-modal-title">
              {intl.formatMessage({ id: "tncModalTitle1" })}
            </h5>
            <h5 id="tnc-modal-title2">
              {intl.formatMessage({ id: "tncModalTitle2" })}
            </h5>
          </StyledTitleSection>

          <StyledCheckboxWrapper>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxClick}
                  inputProps={{
                    "aria-label": intl.formatMessage({
                      id: "tncCheckboxLabel",
                    }),
                    "aria-disabled": !isScrolledToBottom,
                  }}
                  sx={{
                    ...(!isScrolledToBottom && {
                      "&.MuiCheckbox-root .MuiSvgIcon-root": {
                        color: (theme) => theme.colors.shades[300],
                      },
                    }),
                  }}
                />
              }
              label=""
              sx={{ marginTop: "-9px" }}
            />
            <StyledScrollableContent
              ref={scrollRef}
              onScroll={handleScroll}
              tabIndex={0}
              role="region"
              aria-label={intl.formatMessage({ id: "tncContentRegion" })}
              data-error={!isChecked && hasBeenChecked ? "true" : undefined}
            >
              <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
            </StyledScrollableContent>
          </StyledCheckboxWrapper>

          <div aria-live="assertive" aria-atomic="true">
            {showScrollError && (
              <StyledErrorText role="alert">
                {intl.formatMessage({ id: "tncNotReadError" })}
              </StyledErrorText>
            )}
          </div>
        </StyledContentWrapper>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px", justifyContent: "center" }}>
        <Button
          id="tnc-accept-btn"
          onClick={handleAccept}
          aria-disabled={!isChecked}
          sx={(theme) => ({
            width: "auto",
            ...(!isChecked && {
              backgroundColor: theme.colors.dimGray,
              color: theme.colors.shades[400],
              pointerEvents: "none",
            }),
          })}
          tabIndex={0}
        >
          {intl.formatMessage({ id: "tncAcceptButton" })}
        </Button>
      </DialogActions>
      <StyledHelperText>
        {intl.formatMessage({ id: "tncAcceptHelperText" })}
      </StyledHelperText>
    </Dialog>
  );
};

export default TermsAndConditionsModal;
